import SEOHead from '@/components/feature/SEOHead';
import Layout from '@/components/feature/Layout';
import { useParams, Link } from 'react-router-dom';
import { videos } from '@/mocks/videos';
import { articles } from '@/mocks/articles';

const articleSchema = (article: typeof articles[0]) => [
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '日韩在线',
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://rihanzaixian.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '分类浏览',
        item: 'https://rihanzaixian.com/category',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
        item: `https://rihanzaixian.com/article/${article.id}`,
      },
    ],
  },
];

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const videoItem = id ? videos.find((v) => v.id === id) : null;
  const articleItem = id ? articles.find((a) => a.id === id) : null;

  if (!videoItem && !articleItem) {
    return (
      <Layout>
        <div className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-4">内容未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 cursor-pointer">返回首页</Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <SEOHead
        title={videoItem ? `${videoItem.title} - 日韩在线` : `${articleItem?.title} - 日韩在线`}
        description={videoItem ? videoItem.description : articleItem?.excerpt || ''}
        keywords={`日韩在线,${videoItem?.tags?.join(',') || articleItem?.tags?.join(',') || ''},日韩高清在线`}
        canonicalUrl={`https://rihanzaixian.com/article/${id}`}
        ogType="article"
        schemaData={articleItem ? articleSchema(articleItem) : undefined}
      />

      <Layout>
        {videoItem && (
          <article className="pt-20 bg-background-50">
            <div className="max-w-[960px] mx-auto px-4 md:px-6 py-8">
              <nav className="text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <span className="mx-2">/</span>
                <Link to={`/category?tab=${videoItem.category}`} className="hover:text-primary-600 transition-colors cursor-pointer">分类浏览</Link>
                <span className="mx-2">/</span>
                <span className="text-foreground-800 font-medium">{videoItem.title}</span>
              </nav>

              <div className="relative rounded-xl overflow-hidden aspect-video mb-8">
                <img
                  src={videoItem.image}
                  alt={videoItem.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-6 md:p-8">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      {videoItem.tags.map((tag) => (
                        <span key={tag} className="bg-accent-500/80 text-background-50 dark:text-foreground-950 text-xs px-2 py-0.5 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h1 className="text-xl md:text-3xl font-heading font-bold text-background-50 mb-2">
                      {videoItem.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-3 text-xs md:text-sm text-background-200">
                      <span>{videoItem.region}</span>
                      <span>·</span>
                      <span>{videoItem.year}</span>
                      <span>·</span>
                      <span>{videoItem.views}次播放</span>
                      <span>·</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500 text-sm"></i> {videoItem.rating}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-xl p-6 md:p-8 mb-8">
                <h2 className="text-lg font-heading font-bold text-foreground-900 mb-4">内容简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">{videoItem.description}</p>
              </div>

              <div className="text-center">
                <button className="bg-primary-500 text-background-50 dark:text-foreground-950 px-8 py-3 rounded-full text-base font-medium whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 cursor-pointer">
                  <i className="ri-play-circle-fill mr-2"></i>开始观看
                </button>
              </div>
            </div>
          </article>
        )}

        {articleItem && (
          <article className="pt-20 bg-background-50">
            <div className="max-w-[800px] mx-auto px-4 md:px-6 py-8">
              <nav className="text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
                <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">首页</Link>
                <span className="mx-2">/</span>
                <Link to="/category" className="hover:text-primary-600 transition-colors cursor-pointer">分类浏览</Link>
                <span className="mx-2">/</span>
                <span className="text-foreground-800 font-medium">{articleItem.title}</span>
              </nav>

              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {articleItem.tags.map((tag) => (
                    <span key={tag} className="bg-accent-100 text-accent-800 text-xs px-3 py-1 rounded-full font-medium">
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                  {articleItem.title}
                </h1>
                <div className="flex items-center gap-3 text-sm text-foreground-500">
                  <span>{articleItem.author}</span>
                  <span>·</span>
                  <span>{articleItem.date}</span>
                </div>
              </div>

              <div className="relative rounded-xl overflow-hidden aspect-video mb-8">
                <img
                  src={articleItem.image}
                  alt={articleItem.title}
                  className="w-full h-full object-cover"
                />
              </div>

              <div
                className="prose-custom text-sm text-foreground-800 leading-relaxed space-y-4"
                dangerouslySetInnerHTML={{ __html: articleItem.content }}
              />
            </div>
          </article>
        )}
      </Layout>
    </>
  );
}